import {type ChartAggOption} from '../../../generated/graphql';
import {type ColorPaletteOption} from '../../../util/colors';
import {recordRumError} from '../../../util/datadog/datadogRum';
import {
  addSource,
  type LineProperties,
  type LineWithSource,
} from '../../../util/plotHelpers/types';
import {type PrimaryLineType} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {parseHistoryColumnar} from '../bucketedData/columnar/parseColumnarHistory';
import {type LineConfig} from '../config/types';
import {type BucketedLine, type HistorySource} from '../types';
import {type ExcludeOutliersValues} from './../../../components/WorkspaceDrawer/Settings/types';
import {BIN_POINT_TO_USE} from './../../../util/plotHelpers/chartTypes';
import {type Key} from './../../../util/runTypes';
import {type PointWithMeta} from './buildPoints';
import {
  convertLineTemplateToLine,
  convertRunToLineTemplate,
  type ConvertRunToLineTemplateParams,
  type LineTemplate,
} from './convertLines';
import {addSmoothedLineFromBackend} from './lineMutations';
import {parseHistory} from './parseHistory';
import {
  type BackendLine,
  type HistoryColumns,
  type HistoryRecord,
  type LineAggregation,
  type ParseHistoryConfig,
  type SafePoint,
} from './types';
import {columnsHaveMetric, getSafeYKey} from './util';

export type UseLineBehavior = {
  canLogVitals: boolean;
  usingExpressions: boolean;
  useBackendSmoothedLineIfPresent: boolean;
  useBackendLineData: boolean;
};

/**
 * Mutable accumulator for the columnar-parse slice of buildLines, read back by
 * useLines and shipped on the lineConstruction vital. The whole buildLines call
 * is already timed as `parseHistory` (t2-t1); this isolates how much of that is
 * the columnar processing step so we can see it in RUM before optimizing it.
 */
export type ColumnarParseProfile = {
  parseMs: number;
  lineCount: number;
};

/**
 * Suffixes for backend-computed smoothed metrics, checked in priority order.
 * The backend may provide any of these; we use the first one found.
 */
const SMOOTHING_SUFFIXES = [
  '_time_weighted_ema',
  '_ema',
  '_gaussian',
  '_running_average',
] as const;

/** Default smoothing suffix when no backend smoothing is detected */
const DEFAULT_SMOOTHING_SUFFIX = SMOOTHING_SUFFIXES[0];

/**
 * Maps groupArea config values to LineAggregation types for auxiliary lines.
 */
const GROUP_AREA_TO_AGGREGATION: Readonly<Record<string, LineAggregation>> = {
  stddev: 'stdDev',
  stderr: 'stdErr',
  minmax: 'minMax',
};

/**
 * Pre-computed history lookup result for a single metric.
 * Contains all data needed to process a metric without re-scanning history arrays.
 */
export type MetricHistoryEntry = {
  /** The matched history records for this metric, or empty array if not found */
  history: HistoryRecord[];
  /** The matched columnar chunk for this metric (Connect path); takes precedence over `history` */
  columnarChunk?: HistoryColumns;
  /** The bucketed y-key (e.g., 'lossAvg') for accessing values in history records */
  yKey: string;
  /** The smoothed metric name if backend smoothing detected (e.g., 'loss_time_weighted_ema') */
  smoothedMetricName: string;
  /** Whether backend-computed smoothing data is available for this metric */
  hasSmoothedBackendLine: boolean;
};

/**
 * Index mapping metric names to their pre-computed history data for a single run.
 * Built once per run to avoid repeated O(n) history.find() operations.
 */
export type RunHistoryIndex = Map<string, MetricHistoryEntry>;

/**
 * Specification for a line to be materialized.
 * Represents the "seed" data needed to create a Line object.
 * Explicit type replaces the previously inferred object shape from lineSeeds.
 */
export type LineSpec = {
  /** The run's name identifier */
  runName: string;
  /** The runset this run belongs to (undefined for single-runset scenarios) */
  runsetId: string | undefined;
  /** The y-axis metric name (e.g., 'loss', 'accuracy') */
  metric: string;
  /** Index of this metric in the yMetrics array (used for color assignment) */
  metricIndex: number;
  /** Aggregation type: 'Avg' for primary, or groupArea-derived for aux, undefined for 'none' */
  agg: LineAggregation | undefined;
  /** Whether this is an auxiliary (area) line vs primary line */
  isAux: boolean;
  /** Pre-indexed history data for this metric */
  historyEntry: MetricHistoryEntry;
};

/**
 * Builds a lookup index for history arrays by metric name.
 * Called once per run, reducing find() operations from O(2×M) to O(M)
 * where M is the number of metrics.
 *
 * @param run - The bucketed run data containing history arrays
 * @param yMetrics - Array of metric names to index
 * @param useBackendSmoothing - Whether to detect and flag backend smoothing availability
 * @returns Map from metric name to pre-computed history entry
 */
export function buildRunHistoryIndex(
  run: BucketedLine,
  yMetrics: string[],
  useBackendSmoothing: boolean
): RunHistoryIndex {
  const index: RunHistoryIndex = new Map();

  for (const metric of yMetrics) {
    // Columnar runs match a metric to its spec's chunk by probing columns.
    const matchedColumnar = run.historyColumnar?.find(
      chunk => columnsHaveMetric(chunk.cols, metric) || metric in chunk.cols
    );

    // Find the history array that contains this metric
    const matchedHistory =
      run.history.find(history => {
        if (!history[0]) {
          return false;
        }
        const yKey = getSafeYKey({yMetricKey: metric, record: history[0]});
        return yKey in history[0];
      }) ?? [];

    // Bucketed y-key: columnar mirrors getSafeYKey (suffix, else bare key).
    const yKey = matchedColumnar
      ? columnsHaveMetric(matchedColumnar.cols, metric)
        ? `${metric}${BIN_POINT_TO_USE}`
        : metric
      : getSafeYKey({yMetricKey: metric, record: matchedHistory[0]});

    // Detect if backend provides a smoothed version of this metric
    let smoothedMetricName = metric + DEFAULT_SMOOTHING_SUFFIX;
    let hasSmoothedBackendLine = false;

    if (matchedColumnar || matchedHistory.length > 0) {
      for (const suffix of SMOOTHING_SUFFIXES) {
        const metricWithSuffix = metric + suffix;
        const field = yKey.replace(metric, metricWithSuffix);
        const hasSmoothedField = matchedColumnar
          ? field in matchedColumnar.cols
          : field in matchedHistory[0];
        if (hasSmoothedField) {
          smoothedMetricName = metricWithSuffix;
          hasSmoothedBackendLine = useBackendSmoothing;
          break;
        }
      }
    }

    index.set(metric, {
      history: matchedHistory,
      columnarChunk: matchedColumnar,
      yKey,
      smoothedMetricName,
      hasSmoothedBackendLine,
    });
  }

  return index;
}

/**
 * Calculate line opacity based on the estimated number of lines to be rendered.
 * The problem is that thin spikes are hard to detect (so higher opacity), but fat
 * ranges overwhelm the chart with too much visual density. This is preliminary PR
 * to just solve the worst edge case in advance of a better solution:
 * https://weightsandbiases.slack.com/archives/C07JHLX0HJ5/p1760035726213469
 */
function calculateOpacity(lineCount: number): number {
  if (lineCount < 2) {
    return 0.375;
  }

  if (lineCount < 4) {
    return 0.3;
  }

  if (lineCount < 6) {
    return 0.25;
  }

  return 0.2;
}

/**
 * Creates a composite key for template lookup.
 * Format: `${name}-${runsetId}` when runsetId exists, otherwise just `${name}`.
 * This keeps lines from different runsets separate while handling undefined runsetId gracefully.
 * This must match the key format used in templatesByRunKey creation.
 */
export function createLineDataKey(
  runsetId: string | undefined,
  name: string
): string {
  return runsetId ? `${name}-${runsetId}` : name;
}

/**
 * Retrieves a LineTemplate by composite key with error handling.
 * @throws Error if no template found for the given run/runset combination
 */
function getTemplateByRunKey(
  templatesByRunKey: Record<string, LineTemplate>,
  runsetId: string | undefined,
  name: string
): LineTemplate {
  const key = createLineDataKey(runsetId, name);
  const template = templatesByRunKey[key];
  if (!template) {
    throw new Error(
      `No template found for key: ${key} (runsetId: ${runsetId}, name: ${name})`
    );
  }
  return template;
}
/**
 * Note: the `minMax` agg value isn't really used, all we really do is check if it's the Avg or not, otherwise we return aux lines
 */
const aggregations = ['Avg', 'minMax'] as const;
type AggregationPass = (typeof aggregations)[number];

/**
 * Choose which aggregation passes to run for a given primary-line-type.
 *
 * min-max-last mode renders per-bucket min/max/last anchors directly on the
 * primary line, so the shaded area band built from the same min/max fields
 * would double-encode the same information. Drop the 'minMax' pass entirely
 * to suppress the band at the source. The backend `lines(...)` resolver
 * already does the equivalent via `skipArea`; this brings the bucketed-history
 * path to parity.
 */
function getAggregationsForPrimaryLineType(
  primaryLineType: PrimaryLineType | undefined
): readonly AggregationPass[] {
  if (primaryLineType === 'min-max-last') {
    return ['Avg'];
  }
  return aggregations;
}

/**
 * Transforms bucketed run data into renderable Line objects.
 *
 * For each (run, metric) pair, produces up to two lines:
 * - A **primary line** (aggregation: 'avg') containing the main data points
 * - An **auxiliary area** (aggregation per groupArea config: minmax/stddev/stderr)
 *   for variance visualization
 *
 * The function handles both backend-computed lines (preferred for performance)
 * and client-side computation via parseHistory as a fallback.
 *
 * @remarks
 * **NaN Point Coordination**: Non-finite points (NaN, Infinity) discovered during
 * auxiliary line processing are attached to the primary line, not the auxiliary.
 * This requires primary lines to be processed before auxiliary lines—the current
 * implementation relies on 'Avg' appearing before 'minMax' in the aggregations array.
 *
 * **Error Handling**: Lines that fail to materialize are silently dropped.
 * Errors are logged to console for debugging.
 *
 * @returns Array of lines ready for rendering
 */
export function buildLines({
  behavior,
  colorPaletteOption,
  columnarProfile,
  data,
  debugMode,
  excludeOutliers,
  getGroupKeysByRunsetId,
  groupAgg,
  isRunsetGrouped,
  lineConfig,
  panelId,
  primaryLineType,
  xMetrics,
  yMetrics,
}: {
  behavior: UseLineBehavior;
  colorPaletteOption: ColorPaletteOption;
  columnarProfile?: ColumnarParseProfile;
  data: BucketedLine[];
  debugMode: boolean;
  excludeOutliers?: ExcludeOutliersValues;
  getGroupKeysByRunsetId: (runsetId: string | undefined) => Key[];
  groupAgg?: ChartAggOption;
  isRunsetGrouped: (runsetId: string | undefined) => boolean;
  lineConfig: ConvertRunToLineTemplateParams['lineConfig'] &
    Pick<LineConfig, 'runSets' | 'xAxis'>;
  panelId?: string;
  primaryLineType?: PrimaryLineType;
  xMetrics: string[];
  yMetrics: string[];
}): LineWithSource[] {
  /**
   * Calculate opacity based on estimated number of lines to be rendered.
   * This accounts for runs, metrics, and aggregations (Avg + minMax).
   */
  const estimatedLineCount = data.length * yMetrics.length;
  const lineOpacity = calculateOpacity(estimatedLineCount);

  /**
   * Accumulator for non-finite points, shared between primary and auxiliary passes.
   *
   * WHY MUTATION: Avoiding object allocation in this hot path. The accumulator is
   * contained within buildLines, doesn't leak, and push-to-array is amortized O(1).
   * Replacing with immutable returns would add allocation cost for no benefit.
   *
   * WHY SHARED: Both primary (Avg) and auxiliary (minMax/stdDev/stdErr) line processing
   * can discover non-finite points. Rather than attaching NaN markers to both line types
   * (which causes rendering issues downstream), we collect all NaN points into a single
   * list per (run, metric) and attach only to the primary line.
   *
   * INVARIANT: Primary lines must be processed before auxiliary lines so that NaN points
   * discovered during aux processing can be added to the same accumulator. This is
   * enforced by the order of the `aggregations` array: ['Avg', 'minMax'].
   */
  const nfPointsByRunAggMetric: {
    [name: string]: {
      [metric: string]: PointWithMeta[];
    };
  } = {};

  /**
   * Build a map of LineTemplates keyed by composite run key.
   *
   * Templates are factory objects with deferred computation—they hold run metadata
   * and provide functions like `color(metricIndex)` and `meta(agg, isAux)` that
   * are called later when materializing actual lines.
   *
   * IMPORTANT: We key by `runsetId-name` instead of just `name` because:
   * 1. Grouped runs from different runsets can have the same name (e.g., "sweep:")
   * 2. Keying by name only would cause one runset's data to overwrite another's
   */
  const templatesByRunKey = data.reduce(
    function toTemplate(acc, run) {
      const numRunsets = lineConfig.runSets.length;
      const matchedRunset = lineConfig.runSets.find(
        rs => rs.id === run.runsetId
      );
      // Only fall back to first runset if there's exactly one (unambiguous).
      // With multiple runsets, falling back would cause wrong legend/color.
      const runsetInfo =
        matchedRunset ?? (numRunsets === 1 ? lineConfig.runSets[0] : undefined);

      if (!matchedRunset && numRunsets > 1) {
        // Log error when we can't find a matching runset in multi-runset scenarios
        recordRumError(new Error('buildLines: runsetId mismatch'), {
          runsetId: run.runsetId,
          runName: run.name,
          availableRunsetIds: lineConfig.runSets.map(rs => rs.id),
        });
      }

      const runWithRunsetInfo = {
        ...run,
        runsetInfo,
      };

      // Key by runsetId + name to keep lines from different runsets separate
      const key = createLineDataKey(run.runsetId, run.name);
      acc[key] = convertRunToLineTemplate({
        colorPaletteOption,
        excludeOutliers: excludeOutliers,
        groupKeys: getGroupKeysByRunsetId(run.runsetId),
        lineConfig,
        lineOpacity,
        run: runWithRunsetInfo,
      });
      return acc;
    },
    {} as Record<string, LineTemplate>
  );

  /**
   * Build history indices once per run, before the aggregation loop.
   * This reduces O(2 × runs × metrics) history.find() operations to O(runs × metrics).
   *
   * Also initializes NaN point accumulators here rather than inside the aggregation loop,
   * since both Avg and minMax passes need to share the same accumulator arrays.
   */
  const historyIndicesByRun = new Map<string, RunHistoryIndex>();
  for (const run of data) {
    const key = createLineDataKey(run.runsetId, run.name);
    historyIndicesByRun.set(
      key,
      buildRunHistoryIndex(
        run,
        yMetrics,
        behavior.useBackendSmoothedLineIfPresent
      )
    );

    // Initialize NaN point accumulators for this run (shared between Avg and minMax passes)
    nfPointsByRunAggMetric[run.name] = {};
    for (const metric of yMetrics) {
      nfPointsByRunAggMetric[run.name][metric] = [];
    }
  }

  /**
   * The goal in moving this code around is that we want to split apart the logic of creating lines from the logic of creating the history (the data and nanPoints). This is because we want to gradually move to being able to compute these histories on the backend, and then be able to easily switch between the two. It would be nice to do that in a single place abstracted from all the complexity of building the line metadata.
   *
   * The other reason to compute the lines up top is that the rules for when we show certain lines (and thus incur the cost of computing them) can sometimes be known in advance. In the following cases we can safely remove lines:
   * [TODO: the following aren't done but could be]
   * - if it's an original line and smoothing is on and we're not showing originals
   * - if we are excluding outliers (the minMax regions) and thus we don't need the aux lines
   */
  const activeAggregations = getAggregationsForPrimaryLineType(primaryLineType);
  const lineSeeds = activeAggregations.flatMap(
    function buildLineSeedByAgg(type) {
      return data.flatMap(function buildLineSeedByRun(run) {
        const runKey = createLineDataKey(run.runsetId, run.name);
        const historyIndex = historyIndicesByRun.get(runKey)!;

        return yMetrics.map((metric, metricIndex) => {
          const historyEntry = historyIndex.get(metric)!;

          // Determine aggregation type: primary lines use 'Avg', aux lines use groupArea config
          const agg: LineAggregation | undefined =
            type === 'Avg'
              ? 'Avg'
              : GROUP_AREA_TO_AGGREGATION[lineConfig.groupArea];

          return {
            agg,
            isAux: type !== 'Avg',
            smoothedMetric: historyEntry.smoothedMetricName,
            hasSmoothedBackendLine: historyEntry.hasSmoothedBackendLine,
            matchedHistory: historyEntry.history,
            matchedColumnar: historyEntry.columnarChunk,
            metric,
            metricIndex,
            runName: run.name,
            runsetId: run.runsetId,
          };
        });
      });
    }
  );

  const lines = lineSeeds.flatMap(function buildLineFromSeed({
    agg,
    smoothedMetric,
    hasSmoothedBackendLine,
    isAux,
    matchedHistory,
    matchedColumnar,
    metric,
    metricIndex,
    runName,
    runsetId,
  }) {
    if (!agg) {
      // "None" aggregation type -> don't show any min/max bounds
      return [];
    }
    const nfList = nfPointsByRunAggMetric[runName][metric];
    try {
      const template = getTemplateByRunKey(
        templatesByRunKey,
        runsetId,
        runName
      );

      // Track line origin (backend vs frontend) for observability
      let source: LineWithSource['source'] = 'frontend';
      const line = convertLineTemplateToLine(template, {
        agg: agg.toLowerCase() as LineProperties['aggregation'],
        isAux,
        metric,
        metricIndex,
      });

      const ta = performance.now();

      // The purpose of this code block is to attempt to retrieve a backend-provided precomputed line for a given metric and x-axis. If such a backend line is not found, the system logs diagnostic information. This enables graceful fallback to client-side computation while ensuring missing backend support can be tracked and triaged.
      const attemptBackendLine =
        behavior.useBackendLineData && line.historyLines;
      let backendLine: BackendLine | undefined;
      if (attemptBackendLine) {
        backendLine = line.historyLines!.find(
          bl => bl.yMetric === metric && bl.xMetric === lineConfig.xAxis
        );

        if (!backendLine) {
          const yMetricMatch = line.historyLines!.find(
            bl => bl.yMetric === metric
          );
          // DD_RUM.addAction('backend lines not supported', {
          //   reason: 'backend line missing x/y metric match',
          //   yMetric: metric,
          //   xMetric: lineConfig.xAxis,
          //   runId: template.run.id,
          //   hasYMetricMatch: !!yMetricMatch,
          //   xMetricForYMetricMatch: yMetricMatch?.xMetric,
          //   panelId,
          // });
        }
      }

      if (backendLine) {
        source = 'backend';
        line.data = isAux ? backendLine.areaLine : backendLine.historyLine;
        line.historyLines = [];
        if (backendLine.smoothedLine) {
          line.smoothedLineFromBackend = {
            ...line,
            source: 'backend',
            smoothed: true,
            meta: {
              ...line.meta,
              category: 'smoothed',
            },
            data: backendLine.smoothedLine,
          };
        }

        // Preserve pre-calculated domain from backend
        line.domain = {
          minX: backendLine.minX,
          maxX: backendLine.maxX,
          minY: backendLine.minY,
          maxY: backendLine.maxY,
        };
        // Wire backend non-finite points to nanPoints for rendering
        // Only primary lines (non-aux) should show non-finite markers
        line.nanPoints = [];
        if (!isAux) {
          // TODO: come back and clean up this type
          // IT HAS A TICKET TO MAKE SURE IT GETS DONE: https://wandb.atlassian.net/browse/WB-29366
          //
          line.nanPoints = backendLine.nonFiniteLine as any as SafePoint[];
        }
      } else {
        source = 'frontend';
        const historySource: HistorySource = matchedColumnar
          ? {kind: 'columnar', cols: [matchedColumnar]}
          : {kind: 'json', rows: matchedHistory as HistoryRecord[]};

        const otherXMetrics = xMetrics.filter(x => x !== lineConfig.xAxis);
        const parseConfig: ParseHistoryConfig = {
          agg,
          startTime: {
            value: line.startTimeInMs!,
            unit: 'milliseconds',
          },
          usingExpressions: behavior.usingExpressions,
          primaryLineType,
        };
        // we don't allow targeting min/max outside of groups currently
        const parseGroupAgg = isRunsetGrouped(runsetId)
          ? groupAgg
          : (BIN_POINT_TO_USE.toLowerCase() as ChartAggOption);

        // One reader per source; the columnar slice is timed onto columnarProfile
        // so we can see it on the lineConstruction vital before optimizing it.
        const isColumnar = historySource.kind === 'columnar';
        const parseStart = performance.now();
        const {finitePoints, nanPoints} = isColumnar
          ? parseHistoryColumnar(
              historySource.cols,
              metric,
              lineConfig.xAxis,
              otherXMetrics,
              parseConfig,
              parseGroupAgg
            )
          : parseHistory(
              historySource.rows,
              metric,
              lineConfig.xAxis,
              otherXMetrics,
              parseConfig,
              parseGroupAgg
            );
        if (isColumnar && columnarProfile) {
          columnarProfile.parseMs += performance.now() - parseStart;
          columnarProfile.lineCount++;
        }

        line.data = finitePoints;
        line.nanPoints = isAux ? [] : nfList; // aux lines won't get NFs

        const tb = performance.now();
        if (debugMode && tb - ta > 1) {
          console.log('Line creation: parse history: ', tb - ta, 'ms');
        }

        // Push all the NFs into the nanList: because this list reference is created outside the loop here we can mutate it from inside both agg line loops. I don't love this either.
        nfList.push(...nanPoints);

        if (hasSmoothedBackendLine) {
          addSmoothedLineFromBackend(line, {
            agg,
            behavior,
            smoothedMetric,
            finitePoints,
            groupAgg,
            isRunsetGrouped: isRunsetGrouped(runsetId),
            matchedHistory,
            primaryLineType,
            xAxis: lineConfig.xAxis,
            xMetrics,
          });
        }
        const tc = performance.now();
        if (debugMode && tc - tb > 1) {
          console.log(
            'Line creation: add smoothed line from backend: ',
            tc - tb,
            'ms'
          );
        }
      }

      return addSource(line, source);
    } catch (e) {
      recordRumError(e instanceof Error ? e : new Error(String(e)), {
        runName,
        runsetId,
        metric,
        metricIndex,
        aggregation: agg,
        isAux,
        panelId,
        hasBackendLine: hasSmoothedBackendLine,
        historyLength: matchedHistory?.length ?? 0,
        source: 'buildLines',
        phase: 'line_materialization',
      });

      return [];
    }
  });

  return lines;
}
